import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Checkbox } from './ui/checkbox';
import { Alert, AlertDescription } from './ui/alert';
import { UserPlus, AlertCircle, CheckCircle2, Mail, RefreshCw } from 'lucide-react';
import { projectId } from '../utils/supabase/info';

interface Company {
  id: string;
  name: string;
}

interface UserCreateDialogProps {
  open: boolean;
  onClose: () => void;
  onUserCreated: () => void;
  accessToken: string;
  companies: Company[];
  userRole?: string; // Role of the user creating the new user
}

export function UserCreateDialog({ open, onClose, onUserCreated, accessToken, companies, userRole }: UserCreateDialogProps) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [companyId, setCompanyId] = useState('');
  const [role, setRole] = useState('user');
  const [consultantType, setConsultantType] = useState<string>('');
  const [assignedCompanies, setAssignedCompanies] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successInfo, setSuccessInfo] = useState<{
    message: string;
    emailSent: boolean;
    onboardingType?: string;
    emailError?: string;
    userEmail?: string;
  } | null>(null);
  const [resending, setResending] = useState(false);

  // Determine which roles can be assigned based on current user's role
  const isConsultant = userRole === 'consultant';
  const isSystemAdmin = userRole === 'systemAdmin';

  useEffect(() => {
    if (!open) {
      // Reset form when dialog closes
      setEmail('');
      setName('');
      setCompanyId('');
      setRole('user');
      setConsultantType('');
      setAssignedCompanies([]);
      setError('');
      setSuccessInfo(null);
      setResending(false);
    }
  }, [open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (!name.trim()) {
      setError('Name is required');
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/admin/create-user`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            email,
            name: name.trim(),
            companyId,
            role,
            consultantType: role === 'consultant' && consultantType && consultantType !== 'none' ? consultantType : undefined,
            assignedCompanies: role === 'consultant' ? assignedCompanies : [],
          }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to create user');
      }

      // Show success with onboarding email status
      setSuccessInfo({
        message: data.message || 'User created successfully',
        emailSent: data.onboarding?.emailSent ?? false,
        onboardingType: data.onboarding?.onboardingType,
        emailError: data.onboarding?.error,
        userEmail: email,
      });

      onUserCreated();
    } catch (err: any) {
      console.error('Error creating user:', err);
      setError(err.message || 'Failed to create user');
    } finally {
      setLoading(false);
    }
  };

  const handleResendEmail = async () => {
    if (!successInfo?.userEmail) return;
    setResending(true);
    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/admin/resend-onboarding`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`,
          },
          body: JSON.stringify({ email: successInfo.userEmail }),
        }
      );
      const data = await response.json();
      if (response.ok && data.emailSent) {
        setSuccessInfo(prev => prev ? { ...prev, emailSent: true, emailError: undefined } : null);
      } else {
        setSuccessInfo(prev => prev ? { ...prev, emailError: data.error || 'Resend failed' } : null);
      }
    } catch (err: any) {
      setSuccessInfo(prev => prev ? { ...prev, emailError: err.message || 'Resend failed' } : null);
    } finally {
      setResending(false);
    }
  };

  const toggleConsultantCompany = (companyIdToToggle: string) => {
    if (assignedCompanies.includes(companyIdToToggle)) {
      setAssignedCompanies(assignedCompanies.filter(id => id !== companyIdToToggle));
    } else {
      setAssignedCompanies([...assignedCompanies, companyIdToToggle]);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{successInfo ? 'User Created' : 'Create New User'}</DialogTitle>
          <DialogDescription>
            {successInfo
              ? 'The user has been created and notified.'
              : 'Add a new user to the system. The user will receive a confirmation email.'}
          </DialogDescription>
        </DialogHeader>

        {successInfo ? (
          <div className="space-y-4 py-2">
            {successInfo.emailSent && !successInfo.emailError ? (
              <Alert className="border-green-200 bg-green-50">
                <CheckCircle2 className="h-4 w-4 text-green-600" />
                <AlertDescription className="text-green-800">
                  <p className="font-medium">{successInfo.message}</p>
                  <p className="text-sm mt-1">
                    <Mail className="inline h-3 w-3 mr-1" />
                    Onboarding email ({successInfo.onboardingType === 'cel' ? 'Client Engagement Lead' : successInfo.onboardingType === 'team_member' ? 'Team Member' : 'Consultant'}) sent to <strong>{successInfo.userEmail}</strong>
                  </p>
                </AlertDescription>
              </Alert>
            ) : (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  <p className="font-medium">User created, but onboarding email failed.</p>
                  {successInfo.emailError && (
                    <p className="text-sm mt-1">Error: {successInfo.emailError}</p>
                  )}
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="mt-2"
                    onClick={handleResendEmail}
                    disabled={resending}
                  >
                    <RefreshCw className={`mr-2 h-3 w-3 ${resending ? 'animate-spin' : ''}`} />
                    {resending ? 'Resending...' : 'Resend Onboarding Email'}
                  </Button>
                </AlertDescription>
              </Alert>
            )}

            <DialogFooter>
              <Button type="button" onClick={onClose}>
                Done
              </Button>
            </DialogFooter>
          </div>
        ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <div className="space-y-2">
            <Label htmlFor="email">Email Address *</Label>
            <Input
              id="email"
              type="email"
              placeholder="user@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
            <p className="text-muted-foreground">
              A confirmation email will be sent to this address
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="name">Name *</Label>
            <Input
              id="name"
              type="text"
              placeholder="John Smith"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
            <p className="text-muted-foreground">
              The user's full name
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="company">Company *</Label>
            <Select value={companyId} onValueChange={setCompanyId} required>
              <SelectTrigger>
                <SelectValue placeholder="Select a company" />
              </SelectTrigger>
              <SelectContent>
                {companies.map((company) => (
                  <SelectItem key={company.id} value={company.id}>
                    {company.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-muted-foreground">
              The company this user belongs to
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="role">Role *</Label>
            <Select value={role} onValueChange={setRole} required>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="user">Client Team Member</SelectItem>
                {!isConsultant && <SelectItem value="consultant">Consultant</SelectItem>}
                <SelectItem value="companyAdmin">Client Engagement Lead</SelectItem>
                {!isConsultant && <SelectItem value="systemAdmin">System Admin</SelectItem>}
              </SelectContent>
            </Select>
            <p className="text-muted-foreground">
              {role === 'systemAdmin' && 'Full access to all companies and system management'}
              {role === 'companyAdmin' && 'Can manage users within their company'}
              {role === 'consultant' && 'Can access multiple assigned companies'}
              {role === 'user' && 'Standard user access to their company'}
            </p>
            {isConsultant && (
              <p className="text-muted-foreground">
                As a consultant, you can only assign the Client Engagement Lead and Client Team Member roles
              </p>
            )}
          </div>

          {role === 'consultant' && (
            <>
              <div className="space-y-2">
                <Label htmlFor="consultantType">Consultant Specialization (Optional)</Label>
                <Select value={consultantType} onValueChange={setConsultantType}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select specialization (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None (Generalist)</SelectItem>
                    <SelectItem value="bdm">Business Development Manager</SelectItem>
                    <SelectItem value="engagement-lead">Firm Engagement Lead</SelectItem>
                    <SelectItem value="functional">Functional Consultant</SelectItem>
                    <SelectItem value="technical-architect">Technical/Solution Architect</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-muted-foreground">
                  {consultantType === 'bdm' && 'Focuses on sales, intake, and client development'}
                  {consultantType === 'engagement-lead' && 'Owns client delivery and project management'}
                  {consultantType === 'functional' && 'Configures workflows, request types, and business processes'}
                  {consultantType === 'technical-architect' && 'Handles integrations and complex technical configuration'}
                  {(!consultantType || consultantType === 'none') && 'This consultant can perform any type of work'}
                </p>
              </div>

              <div className="space-y-2">
                <Label>Consultant Company Access</Label>
                <p className="text-muted-foreground">
                  Select which companies this consultant can access
                </p>
                <div className="border rounded-lg p-4 space-y-2 max-h-60 overflow-y-auto">
                  {companies.map((company) => (
                    <div key={company.id} className="flex items-center space-x-2">
                      <Checkbox
                        id={`consultant-company-${company.id}`}
                        checked={assignedCompanies.includes(company.id)}
                        onCheckedChange={() => toggleConsultantCompany(company.id)}
                      />
                      <Label 
                        htmlFor={`consultant-company-${company.id}`}
                        className="cursor-pointer flex-1"
                      >
                        {company.name}
                      </Label>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button 
              type="submit" 
              disabled={loading || !email.trim() || !name.trim()}
            >
              <UserPlus className="mr-2 h-4 w-4" />
              {loading ? 'Creating User...' : 'Create User'}
            </Button>
          </DialogFooter>
        </form>
        )}
      </DialogContent>
    </Dialog>
  );
}